/** The Live Lookups SVG scene — the user's device and the pathways to each of
 *  our nameserver locations. Each node shows the browser-measured round-trip
 *  time to that location and how many of the test's queries arrived there.
 *  Geometry, device glyph, packet motion and flash are the mockup's. */
import * as React from "react";
import type { Nameserver, Phase } from "../lib/types";
import type { Fired } from "../lib/useLeakTest";
import { DOMAINS } from "../lib/scenarios";

interface Pt { x: number; y: number }

export interface LookupSceneProps {
  phase: Phase;
  /** ms since the run started. */
  now: number;
  fired: Fired[];
  nameservers: Nameserver[];
  pings: Record<string, number | null>;
  nsCounts: Record<string, number>;
  reducedMotion: boolean;
  vw: number;
}

function bezier(P: Pt[], u: number): Pt {
  const m = 1 - u, a = m * m * m, b = 3 * m * m * u, c = 3 * m * u * u, d = u * u * u;
  const [p0, p1, p2, p3] = P as [Pt, Pt, Pt, Pt];
  return { x: a * p0.x + b * p1.x + c * p2.x + d * p3.x, y: a * p0.y + b * p1.y + c * p2.y + d * p3.y };
}

export function LookupScene(props: LookupSceneProps): React.ReactElement {
  const R = React;
  const { phase, now, fired, nameservers, pings, nsCounts, reducedMotion: rm, vw } = props;
  const running = phase === "running";
  const n = Math.max(1, nameservers.length);
  const FLIGHT = 520;      // ms a packet takes to reach its nameserver
  const GLOW = 650;        // ms a nameserver stays lit after arrival

  const W = 660, H = 214;
  const dev: Pt = { x: 124, y: 107 };
  const NX = 452, NW = 196;
  // Node y positions spread across the height; 3 nodes → 40 / 107 / 174.
  const ys = nameservers.map((_, i) => (n === 1 ? 107 : 40 + (i * 134) / (n - 1)));
  const pathFor = (y: number): Pt[] => [dev, { x: 300, y: 107 }, { x: 330, y }, { x: NX, y }];
  const pathD = (P: Pt[]) => `M${P[0]!.x},${P[0]!.y} C${P[1]!.x},${P[1]!.y} ${P[2]!.x},${P[2]!.y} ${P[3]!.x},${P[3]!.y}`;

  const regionIndex = (id: string) => Math.max(0, nameservers.findIndex((x) => x.id === id));
  const inFlight = running ? fired.filter((f) => now - f.at >= 0 && now - f.at < FLIGHT) : [];
  const litUntil = (id: string) => {
    const arrivals = fired.filter((f) => f.region === id).map((f) => f.at + FLIGHT);
    return arrivals.some((a) => now >= a && now < a + GLOW);
  };

  const els: React.ReactNode[] = [];
  ys.forEach((y, i) => els.push(R.createElement("path", { key: `p${i}`, d: pathD(pathFor(y)), fill: "none", stroke: "rgba(43,232,43,0.22)", strokeWidth: 1.5 })));

  const isMobile = (vw || 1200) <= 760;
  const dcx = 107, dcy = 107, ringR = 7;
  const ring = (cx: number, cy: number) => [
    R.createElement("circle", { key: "r1", cx, cy, r: ringR, fill: "none", stroke: running ? "#2BE82B" : "#3A3E39", strokeWidth: 2.5, style: running && !rm ? { filter: "drop-shadow(0 0 5px rgba(43,232,43,0.8))" } : undefined }),
    R.createElement("circle", { key: "r2", cx, cy, r: 2.5, fill: running ? "#2BE82B" : "#3A3E39" }),
  ];
  let glyph: React.ReactNode[];
  if (isMobile) {
    const pw = 60, ph = 90, px = dcx - pw / 2, py = dcy - ph / 2;
    glyph = [
      R.createElement("rect", { key: "body", x: px, y: py, width: pw, height: ph, rx: 15, fill: "#0A0B0A", stroke: "#2A2D29", strokeWidth: 1.5 }),
      R.createElement("rect", { key: "scr", x: px + 8, y: py + 12, width: pw - 16, height: ph - 30, rx: 7, fill: "#141614" }),
      R.createElement("rect", { key: "notch", x: dcx - 9, y: py + 6, width: 18, height: 3.5, rx: 1.75, fill: "#2A2D29" }),
      ...ring(dcx, py + 12 + (ph - 30) / 2 - 3),
      R.createElement("rect", { key: "home", x: dcx - 12, y: py + ph - 12, width: 24, height: 3.5, rx: 1.75, fill: "#2A2D29" }),
    ];
  } else {
    const sw = 94, sh = 62, sx = dcx - sw / 2, sy = dcy - sh / 2 - 6;
    glyph = [
      R.createElement("rect", { key: "body", x: sx, y: sy, width: sw, height: sh, rx: 8, fill: "#0A0B0A", stroke: "#2A2D29", strokeWidth: 1.5 }),
      R.createElement("rect", { key: "scr", x: sx + 8, y: sy + 8, width: sw - 16, height: sh - 16, rx: 4, fill: "#141614" }),
      ...ring(dcx, sy + sh / 2),
      R.createElement("path", { key: "base", d: `M${sx - 12},${sy + sh + 12} L${sx + sw + 12},${sy + sh + 12} L${sx + sw + 2},${sy + sh} L${sx - 2},${sy + sh} Z`, fill: "#141614", stroke: "#2A2D29", strokeWidth: 1.5, strokeLinejoin: "round" }),
      R.createElement("rect", { key: "notch", x: dcx - 11, y: sy + sh + 3, width: 22, height: 3, rx: 1.5, fill: "#2A2D29" }),
    ];
  }
  els.push(R.createElement("g", { key: "dev" }, ...glyph,
    R.createElement("text", { x: dcx, y: 194, textAnchor: "middle", fill: "#5C5F5A", style: { font: "11px 'Space Mono', monospace" } }, "your device")));

  nameservers.forEach((ns, i) => {
    const y = ys[i] ?? 107;
    const got = nsCounts[ns.id] ?? 0;
    const ms = pings[ns.id];
    const on = running && litUntil(ns.id);
    const color = "#2BE82B";
    const msText = ms == null ? (phase === "idle" ? "" : "measuring…") : `${ms} ms`;
    const sub = `${msText}${msText && (phase !== "idle") ? " · " : ""}${phase === "idle" ? "" : `${got} ${got === 1 ? "query" : "queries"}`}`;
    els.push(R.createElement("g", { key: ns.id },
      R.createElement("rect", { x: NX, y: y - 20, width: NW, height: 40, rx: 12, fill: "#0A0B0A", stroke: on ? color : "rgba(255,255,255,0.12)", strokeWidth: on ? 1.5 : 1, style: on && !rm ? { filter: `drop-shadow(0 0 6px ${color}66)` } : undefined }),
      R.createElement("circle", { cx: NX + 16, cy: y, r: 4, fill: on ? color : "#3A3E39" }),
      R.createElement("text", { x: NX + 30, y: y - 3, fill: on || got > 0 ? "#DEDFDF" : "#5C5F5A", style: { font: "11px 'Space Mono', monospace" } }, ns.label),
      R.createElement("text", { x: NX + 30, y: y + 12, fill: on ? color : got > 0 ? "#B9BCB6" : "#5C5F5A", style: { font: "10px 'Space Mono', monospace" } }, sub || "nameserver")));
  });

  // Arrival flash rings + packets in flight (several may overlap in a wave).
  for (const f of running ? fired : []) {
    const age = now - f.at - FLIGHT;
    if (age >= 0 && age < 220) {
      const end = pathFor(ys[regionIndex(f.region)] ?? 107)[3]!;
      const k = age / 220;
      els.push(R.createElement("circle", { key: `fl${f.idx}`, cx: end.x, cy: end.y, r: 6 + k * 14, fill: "none", stroke: "#2BE82B", strokeWidth: 2, opacity: 1 - k }));
    }
  }
  for (const f of inFlight) {
    const p = (now - f.at) / FLIGHT;
    const pkt = bezier(pathFor(ys[regionIndex(f.region)] ?? 107), p);
    const domain = DOMAINS[f.idx % DOMAINS.length] ?? "";
    if (!rm) els.push(R.createElement("circle", { key: `bc${f.idx}`, cx: pkt.x, cy: pkt.y, r: 4 + p * 10, fill: "none", stroke: "#FFFFFF", strokeWidth: 1.5, opacity: 0.6 * (1 - p) }));
    els.push(R.createElement("circle", { key: `pk${f.idx}`, cx: pkt.x, cy: pkt.y, r: 5, fill: "#FFFFFF", style: rm ? undefined : { filter: "drop-shadow(0 0 6px rgba(255,255,255,0.9))" } }));
    els.push(R.createElement("text", { key: `lb${f.idx}`, x: pkt.x, y: pkt.y - 14, textAnchor: "middle", fill: "#2BE82B", style: { font: "11px 'Space Mono', monospace" } }, domain));
  }

  return R.createElement("svg", { viewBox: `0 0 ${W} ${H}`, preserveAspectRatio: "xMidYMid meet", style: { width: "100%", height: "auto", maxHeight: 230, display: "block" } }, els);
}
